import { HttpErrorResponse } from '@angular/common/http';
import { computed, inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, switchMap, tap } from 'rxjs';

import {
  setPropError,
  setPropInit,
  setPropLoaded,
  setPropLoading,
  withReqState,
} from '@application/data-access';
import { ErrorStore } from '@application/errors/data-access';
import {
  PermissionNumber,
  SelfUserStore,
} from '@domains/person/user/self/data';

import {
  TPersonNegativation,
  TPersonNegativationHistory,
} from './entities/person-negativation.model';
import { PersonNegativationService } from './infra/person-negativation.service';

const OPS = [
  'getNegativation',
  'setNegativation',
  'removeNegativation',
  'getHistory',
] as const;

export interface IPersonNegativationStore {
  personId: string | null;
  negativation: TPersonNegativation | null;
  history: TPersonNegativationHistory[];
}

export const initialPersonNegativationStore: IPersonNegativationStore = {
  personId: null,
  negativation: null,
  history: [],
};

/**
 * Estado da negativação manual do cliente em foco.
 *
 * A permissão 291 controla **alterar** a marcação, não **enxergá-la**: quem
 * acessa a ficha vê badge, observação e histórico sem a permissão — só os
 * comandos de ativar/remover ficam indisponíveis. Por isso as leituras aqui não
 * checam permissão; só `canManage` é exposto para a UI esconder os comandos.
 */
export const PersonNegativationStore = signalStore(
  { providedIn: 'root' },
  withState<IPersonNegativationStore>(initialPersonNegativationStore),
  withReqState(...OPS),
  withComputed((store, selfUserStore = inject(SelfUserStore)) => ({
    isNegativated: computed<boolean>(
      () => store.negativation()?.IsNegativated ?? false
    ),
    note: computed<string | null>(() => store.negativation()?.Note ?? null),
    /** Conveniência de UI. O servidor valida a permissão em toda escrita, inclusive por chamada direta. */
    canManage: computed<boolean>(() =>
      selfUserStore.hasClientPermission(
        PermissionNumber.ClienteNegativacaoGerenciar
      )
    ),
  })),
  withMethods(
    (
      store,
      service = inject(PersonNegativationService),
      errorStore = inject(ErrorStore)
    ) => {
      const getHistory = rxMethod<string>(
        pipe(
          tap(() => patchState(store, { ...setPropLoading('getHistory') })),
          switchMap((personId) =>
            service.getHistory(personId).pipe(
              tapResponse({
                next: (history) =>
                  patchState(store, { history }, setPropLoaded('getHistory')),
                error: (error: HttpErrorResponse) => {
                  patchState(store, { ...setPropError('getHistory', error) });
                  errorStore.setHttpError(error);
                },
              })
            )
          )
        )
      );

      /** As três escritas devolvem a situação atual; o histórico é relido em seguida. */
      const applyResult = (
        op: 'setNegativation' | 'removeNegativation',
        negativation: TPersonNegativation
      ): void => {
        patchState(
          store,
          { personId: negativation.PersonId, negativation },
          setPropLoaded(op)
        );
        getHistory(negativation.PersonId);
      };

      return {
        getNegativation: rxMethod<string>(
          pipe(
            tap(() =>
              patchState(store, { ...setPropLoading('getNegativation') })
            ),
            switchMap((personId) =>
              service.getNegativation(personId).pipe(
                tapResponse({
                  next: (negativation) =>
                    patchState(
                      store,
                      { personId, negativation },
                      setPropLoaded('getNegativation')
                    ),
                  error: (error: HttpErrorResponse) => {
                    patchState(store, {
                      ...setPropError('getNegativation', error),
                    });
                    errorStore.setHttpError(error);
                  },
                })
              )
            )
          )
        ),

        /** Ativa a marcação, ou altera só a observação de quem já está marcado. */
        setNegativation: rxMethod<{ personId: string; note: string | null }>(
          pipe(
            tap(() =>
              patchState(store, { ...setPropLoading('setNegativation') })
            ),
            switchMap(({ personId, note }) =>
              service.setNegativation(personId, note).pipe(
                tapResponse({
                  next: (negativation) =>
                    applyResult('setNegativation', negativation),
                  error: (error: HttpErrorResponse) => {
                    patchState(store, {
                      ...setPropError('setNegativation', error),
                    });
                    errorStore.setHttpError(error);
                  },
                })
              )
            )
          )
        ),

        removeNegativation: rxMethod<string>(
          pipe(
            tap(() =>
              patchState(store, { ...setPropLoading('removeNegativation') })
            ),
            switchMap((personId) =>
              service.removeNegativation(personId).pipe(
                tapResponse({
                  next: (negativation) =>
                    applyResult('removeNegativation', negativation),
                  error: (error: HttpErrorResponse) => {
                    patchState(store, {
                      ...setPropError('removeNegativation', error),
                    });
                    errorStore.setHttpError(error);
                  },
                })
              )
            )
          )
        ),

        getHistory,

        clearMutationState: (): void =>
          patchState(
            store,
            setPropInit('setNegativation', 'removeNegativation')
          ),

        reset: (): void =>
          patchState(
            store,
            initialPersonNegativationStore,
            setPropInit(...OPS)
          ),
      };
    }
  )
);
